<template>
  <div ref="containerRef" class="logo-medical-animation">
    <canvas ref="canvasRef" class="logo-medical-canvas" />
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import * as THREE from 'three'

const containerRef = ref(null)
const canvasRef = ref(null)

let renderer = null
let scene = null
let camera = null
let group = null
let animationId = null
let resizeObserver = null
let clock = null

const TEX_SIZE = 1024
const CROSS_RADIUS = 2.0
const HALF_TURN_SECONDS = 2.5

const slides = []
const materials = { matA: null, matB: null }
const state = { idxA: 0, idxB: 1, lastBoundary: 0 }

function makeContentTexture(drawFn) {
  const cv = document.createElement('canvas')
  cv.width = TEX_SIZE
  cv.height = TEX_SIZE
  const ctx = cv.getContext('2d')
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, TEX_SIZE, TEX_SIZE)
  drawFn(ctx)
  const tex = new THREE.CanvasTexture(cv)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 8
  return tex
}

function makeTextTexture(text) {
  return makeContentTexture((ctx) => {
    ctx.fillStyle = '#0a3fa0'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    let fontSize = 180
    ctx.font = `bold ${fontSize}px Arial, sans-serif`
    while (ctx.measureText(text).width > TEX_SIZE * 0.72 && fontSize > 30) {
      fontSize -= 6
      ctx.font = `bold ${fontSize}px Arial, sans-serif`
    }
    ctx.fillText(text, TEX_SIZE / 2, TEX_SIZE / 2)
  })
}

/**
 * Load the real logo PNG as a texture.
 * Falls back to simple canvas text if loading fails.
 */
function loadLogoTexture() {
  return new Promise((resolve) => {
    const loader = new THREE.TextureLoader()
    const logoUrl = (import.meta.env.BASE_URL || '/') + 'images/korkem-logo.jpg'
    loader.load(
      logoUrl,
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace
        tex.anisotropy = 8
        resolve(tex)
      },
      undefined,
      () => {
        resolve(makeContentTexture((ctx) => {
          ctx.fillStyle = '#0a3fa0'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.font = 'bold italic 200px Arial, sans-serif'
          ctx.fillText('Korkem', TEX_SIZE / 2, TEX_SIZE / 2 - 40)
          ctx.font = 'bold 70px Arial, sans-serif'
          ctx.fillText('АВТОМЕКТЕП', TEX_SIZE / 2, TEX_SIZE / 2 + 80)
        }))
      }
    )
  })
}

/** Build a plus/cross Shape for ShapeGeometry */
function buildCrossShape(L, T) {
  const shape = new THREE.Shape()
  shape.moveTo(-T, -L)
  shape.lineTo( T, -L)
  shape.lineTo( T, -T)
  shape.lineTo( L, -T)
  shape.lineTo( L,  T)
  shape.lineTo( T,  T)
  shape.lineTo( T,  L)
  shape.lineTo(-T,  L)
  shape.lineTo(-T,  T)
  shape.lineTo(-L,  T)
  shape.lineTo(-L, -T)
  shape.lineTo(-T, -T)
  shape.lineTo(-T, -L)
  return shape
}

function resize() {
  if (!renderer || !camera || !containerRef.value) return
  const w = containerRef.value.clientWidth || 1
  const h = containerRef.value.clientHeight || 1
  renderer.setSize(w, h, false)
  camera.aspect = w / h
  camera.updateProjectionMatrix()
}

function animate() {
  animationId = requestAnimationFrame(animate)
  if (!clock || !group || !renderer || !scene || !camera) return

  const t = clock.getElapsedTime()
  const angle = (t / HALF_TURN_SECONDS) * Math.PI
  group.rotation.y = angle

  const boundary = Math.floor(angle / Math.PI)
  if (boundary !== state.lastBoundary) {
    state.lastBoundary = boundary
    const visibleIsB = (boundary % 2) === 1
    const visibleIdx = visibleIsB ? state.idxB : state.idxA
    const nextIdx = (visibleIdx + 1) % slides.length
    if (visibleIsB) {
      state.idxA = nextIdx
      materials.matA.map = slides[state.idxA]
      materials.matA.needsUpdate = true
    } else {
      state.idxB = nextIdx
      materials.matB.map = slides[state.idxB]
      materials.matB.needsUpdate = true
    }
  }

  renderer.render(scene, camera)
}

onMounted(async () => {
  const canvas = canvasRef.value
  renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true })
  renderer.setPixelRatio(window.devicePixelRatio)
  renderer.setClearColor(0xffffff, 1)

  scene = new THREE.Scene()
  scene.background = new THREE.Color(0xffffff)

  camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100)
  camera.position.set(0, 0, 8)
  camera.lookAt(0, 0, 0)

  scene.add(new THREE.AmbientLight(0xffffff, 1.0))

  const logoTex = await loadLogoTexture()
  slides.push(
    logoTex,
    makeTextTexture('СУРЕТ ЖОҚ'),
    makeTextTexture('БЕЗ КАРТИНКИ'),
    makeTextTexture('NO IMAGE')
  )

  group = new THREE.Group()
  scene.add(group)

  // Cross-shaped geometry
  const L = CROSS_RADIUS
  const T = CROSS_RADIUS * 0.35
  const crossShape = buildCrossShape(L, T)
  const crossGeo = new THREE.ShapeGeometry(crossShape)

  // Normalize UVs to map texture across bounding box
  const pos = crossGeo.attributes.position
  const uv = new Float32Array(pos.count * 2)
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i)
    const y = pos.getY(i)
    uv[i * 2] = (x + L) / (2 * L)
    uv[i * 2 + 1] = (y + L) / (2 * L)
  }
  crossGeo.setAttribute('uv', new THREE.BufferAttribute(uv, 2))

  materials.matA = new THREE.MeshBasicMaterial({ map: slides[0], side: THREE.FrontSide })
  materials.matB = new THREE.MeshBasicMaterial({ map: slides[1], side: THREE.FrontSide })

  const discA = new THREE.Mesh(crossGeo, materials.matA)
  const discB = new THREE.Mesh(crossGeo, materials.matB)
  discB.rotation.y = Math.PI // back-to-back
  group.add(discA)
  group.add(discB)

  // Red outline tracing the cross
  const edgePoints = [
    [-T, -L], [T, -L], [T, -T], [L, -T], [L, T], [T, T],
    [T, L], [-T, L], [-T, T], [-L, T], [-L, -T], [-T, -T], [-T, -L]
  ].map(([x, y]) => new THREE.Vector3(x, y, 0))
  const ringGeo = new THREE.BufferGeometry().setFromPoints(edgePoints)
  const ringMat = new THREE.LineBasicMaterial({ color: 0xff3434 })
  const ring = new THREE.LineLoop(ringGeo, ringMat)
  group.add(ring)

  clock = new THREE.Clock()
  resize()

  if (typeof ResizeObserver !== 'undefined' && containerRef.value) {
    resizeObserver = new ResizeObserver(resize)
    resizeObserver.observe(containerRef.value)
  } else {
    window.addEventListener('resize', resize)
  }

  animate()
})

onBeforeUnmount(() => {
  if (animationId !== null) {
    cancelAnimationFrame(animationId)
    animationId = null
  }
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  } else {
    window.removeEventListener('resize', resize)
  }

  slides.forEach((tex) => tex?.dispose?.())
  slides.length = 0

  if (group) {
    group.traverse((obj) => {
      if (obj.geometry) obj.geometry.dispose()
      if (obj.material) {
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material]
        mats.forEach((m) => m.dispose?.())
      }
    })
  }

  renderer?.dispose?.()
  renderer = null
  scene = null
  camera = null
  group = null
  clock = null
})
</script>

<style scoped>
.logo-medical-animation {
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: 75vh;
  background: #ffffff;
  display: block;
  position: relative;
}
.logo-medical-canvas {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
